// Calculele pure ale dashboard-ului `Protecția datelor`: procente, bare și
// grupări. Nicio cifră nu se inventează în UI; totul derivă din răspunsul API-ului.

// Stările proiecțiilor, în ordinea în care apar în bare: pozitive, neutre, negative.
export const PAIR_STATE_ORDER = Object.freeze([
  "granted",
  "other_basis",
  "pending_confirmation",
  "expired",
  "refused",
  "withdrawn",
  "objected",
  "restricted",
]);

export const NEGATIVE_PAIR_STATES = Object.freeze(["refused", "withdrawn", "objected", "restricted"]);

// Culorile derivă numai din tokenurile temei. Stările negative diferite rămân
// distinse prin nuanță, nu prin alt hue, ca roșul să însemne „alegere negativă".
const tone = (token, darken = 0) => (darken
  ? `color-mix(in srgb, rgb(var(--v-theme-${token})) ${100 - darken}%, black)`
  : `rgb(var(--v-theme-${token}))`);

export const PAIR_STATE_COLORS = Object.freeze({
  granted: tone("success"),
  other_basis: tone("info"),
  pending_confirmation: tone("warning"),
  expired: tone("warning", 25),
  refused: tone("error"),
  withdrawn: tone("error", 30),
  objected: tone("error", 45),
  restricted: tone("error", 15),
});

// `undocumented` și `not_applicable` rămân neutre (`GDPR-042`).
export const SUMMARY_STATE_COLORS = Object.freeze({
  active: tone("success"),
  partial: tone("warning"),
  pending: "color-mix(in srgb, rgb(var(--v-theme-warning)) 55%, rgb(var(--v-theme-surface)))",
  withdrawn_or_refused: tone("error"),
  other_basis: tone("info"),
  undocumented: "rgba(var(--v-theme-on-surface), 0.28)",
  not_applicable: "rgba(var(--v-theme-on-surface), 0.14)",
});

// Sub 1% dar nenul se afișează „<1", nu „0": altfel un caz real ar părea absent.
export const sharePercent = (value, total) => {
  const count = Number(value || 0);
  const whole = Number(total || 0);
  if (!whole || !count) return "0";
  const percent = (count / whole) * 100;
  return percent < 1 ? "<1" : String(Math.round(percent));
};

export const pairTotals = (byState = {}) => {
  const negativeParts = NEGATIVE_PAIR_STATES
    .map((state) => ({ state, count: Number(byState[state] || 0) }))
    .filter(({ count }) => count > 0);
  return {
    granted: Number(byState.granted || 0),
    other_basis: Number(byState.other_basis || 0),
    negative: negativeParts.reduce((sum, { count }) => sum + count, 0),
    negativeParts,
  };
};

// Barele dintr-un grup sunt scalate la cel mai mare total al grupului, ca
// lungimea să compare volume; segmentele împart fiecare bară după stare.
export const pairRows = (rows = [], keyField, labelOf) => {
  const totals = rows.map((row) => PAIR_STATE_ORDER
    .reduce((sum, state) => sum + Number(row.by_state?.[state] || 0), 0));
  const max = Math.max(0, ...totals);
  return rows
    .map((row, index) => ({
      key: String(row[keyField]),
      label: labelOf(row),
      total: totals[index],
      widthPct: max ? (totals[index] / max) * 100 : 0,
      segments: PAIR_STATE_ORDER
        .map((state) => ({ state, count: Number(row.by_state?.[state] || 0) }))
        .filter(({ count }) => count > 0)
        .map((segment) => ({ ...segment, pct: (segment.count / totals[index]) * 100 })),
    }))
    .filter((row) => row.total > 0);
};

export const pairLegendStates = (groups) => PAIR_STATE_ORDER
  .filter((state) => groups.some((rows) => rows.some((row) => row.segments.some((s) => s.state === state))));

export const breakdownRows = (byState = {}, total, order) => {
  const rows = order.map((state) => ({
    state,
    count: Number(byState[state] || 0),
    pct: sharePercent(byState[state], total),
    widthPct: total ? (Number(byState[state] || 0) / total) * 100 : 0,
  }));
  return {
    present: rows.filter(({ count }) => count > 0),
    empty: rows.filter(({ count }) => count === 0).map(({ state }) => state),
  };
};
